import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import MoneySchema from "_shared/money/money-schema";
import { z } from "zod";

export type FeeJSON = z.infer<typeof FeeSchema.FeeJSONZ>;
export type FeeParams = z.infer<typeof FeeSchema.FeeZ>;

export default class FeeSchema {
  public static readonly FeeJSONZ = z.object({
    rate: MoneySchema.type,
    unit: z.string().nullish()
  });

  public static readonly FeeZ = z.object({
    rate: MoneySchema.type,
    unit: z.string().nullable()
  });

  public static validate(values: unknown): FeeParams {
    return parseOrThrow(
      FeeSchema.FeeZ,
      values,
      { 
        logError: true,
        schemaName: "FeeSchema",
        operation: "validate",
        payloadSample: values 
      }
    );
  }

  public static validateJSON(json: unknown): FeeJSON {
    return parseOrThrow(
      FeeSchema.FeeJSONZ,
      json,
      { 
        logError: true,
        schemaName: "FeeSchema",
        operation: "validateJSON",
        payloadSample: json 
      }
    );
  }

  public static isValid(values: unknown): values is FeeParams {
    try {
      FeeSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is FeeJSON {
    try {
      FeeSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: FeeJSON): FeeParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(
      FeeSchema.FeeJSONZ,
      json,
      { 
        logError: true,
        schemaName: "FeeSchema",
        operation: "deserialize",
        payloadSample: json 
      }
    );
    return FeeSchema.codec.decode(parsed);
  }

  public static serialize(values: FeeParams): FeeJSON {
    if (isNullOrUndefined(values)) return values;
    const parsed = FeeSchema.validate(values);
    if (isNullOrUndefined(parsed)) return parsed;
    return FeeSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    FeeSchema.FeeJSONZ,
    FeeSchema.FeeZ, {
    decode: (data) => ({
      rate: data.rate,
      unit: data.unit ?? null
    }),
    encode: (fee) => ({
      rate: fee.rate,
      unit: fee.unit
    })
  });
}
